import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandBand, Button, Icon, IconSettings, PageHeader, PageHeaderControl } from '@salesforce/design-system-react';
import CQGlobalHeader from 'components/cq-global-header';
import CQPushPortal from 'components/cq-push-portal';
import CQStencil from 'components/cq-stencil';
import { PushPortalServiceProvider } from 'services/push-portal.service';
import { validateTagNames } from 'common/tag.utils';
import { SFConnection } from 'common/sf-connector';
import { CQAppConstant } from 'app-constants';
import { useTranslation } from 'react-i18next';
import { getInstallUrl } from 'common/utils';

/**
 * Push Portal page wrapper.
 *
 * Handles permissions, OAuth messaging, and renders the main Push Portal UI.
 */
function CQPushPortalPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [portalPermissions, setPortalPermissions] = useState<{ canView: boolean; canSchedule: boolean; canRegister: boolean }>({ canView: false, canSchedule: false, canRegister: false });
    const [isRegisterOpen, setIsRegisterOpen] = useState(false);
    const [oauthLoginUrl, setOauthLoginUrl] = useState(CQAppConstant.DEFAULT_URLS.SF_LOGIN);
    const [regLoading, setRegLoading] = useState(false);
    const [regError, setRegError] = useState('');
    const [regSuccess, setRegSuccess] = useState('');
    const [currentUsername, setCurrentUsername] = useState('');
    const [currentUserEmail, setCurrentUserEmail] = useState('');
    const [registerTagNames, setRegisterTagNames] = useState('');
    const [registerTagError, setRegisterTagError] = useState('');
    const [showPackageInterstitial, setShowPackageInterstitial] = useState(false);

    useEffect(() => {
        PushPortalServiceProvider.getPermissions()
            .then(perms => setPortalPermissions(perms))
            .catch(() => setPortalPermissions({ canView: false, canSchedule: false, canRegister: false }))
            .finally(() => setIsLoading(false));
        fetchCurrentUser();

        const params = new URLSearchParams(window.location.search);
        const registered = params.get('registered');
        const urlError = params.get('error');
        if (registered) {
            setRegSuccess(`Org "${registered}" registered successfully`);
            window.history.replaceState({}, '', window.location.pathname);
            setTimeout(() => setRegSuccess(''), 10000);
        } else if (urlError) {
            setRegError(decodeURIComponent(urlError));
            window.history.replaceState({}, '', window.location.pathname);
            setTimeout(() => setRegError(''), 10000);
        }
    }, []);

    if (isLoading) {
        return <CQStencil />;
    }

    /**
     * Renders the title section of the pushPortal mainpage.
     *
     * This function returns a JSX element containing a vertical grid layout
     * with a main header and an info
     *
     * @returns {JSX.Element} A JSX element representing the title section.
     */
    function renderTitle() {
        return (
            <div className="slds-grid slds-grid_vertical">
                <h1>{t("push_portal_pageheader")}</h1>
                <p className="slds-line-clamp slds-text-body_regular slds-m-vertical_xx-small">
                    {t("push_portal_header_info")}
                </p>
            </div>
        );
    }

    // Render page header actions (register, refresh, back).
    const onRenderActions = () => (
        <PageHeaderControl>
            <div className="slds-grid slds-grid_vertical-align-center">
                {portalPermissions.canRegister && (
                    <Button
                        label="Register Org"
                        variant="brand"
                        className="slds-m-right_x-small"
                        onClick={() => { setIsRegisterOpen(prev => !prev); setRegError(''); }}
                        disabled={regLoading}
                    />
                )}
                <Button
                    label="Refresh"
                    variant="neutral"
                    className="slds-m-right_x-small"
                    onClick={() => {
                        // Call activity refresh if available, otherwise full page reload
                        if ((window as any).__pushPortalRefresh) {
                            (window as any).__pushPortalRefresh();
                        } else {
                            window.location.reload();
                        }
                    }}
                />
                <Button
                    label={t("back_to_deployment_agent")}
                    variant="neutral"
                    onClick={() => navigate('/')}
                />
            </div>
        </PageHeaderControl>
    );

    return (
        <>
            <CQGlobalHeader />
            <div className="slds-m-top_xx-large">
                <IconSettings iconPath="/assets/icons">
                    <BrandBand id="brand-band-push-portal" className="slds-p-around_small">
                        <PageHeader
                            icon={
                                <Icon
                                    assistiveText={{ label: 'Announcement' }}
                                    category="utility"
                                    name="announcement"
                                    colorVariant="warning"
                                    title={t("push_portal_pageheader")}
                                />
                            }
                            onRenderActions={onRenderActions}
                            title={renderTitle()}
                            variant="object-home"
                        />
                        {regSuccess && (
                            <div className="slds-notify slds-notify_alert slds-theme_success slds-m-bottom_medium" role="alert">
                                <span>{regSuccess}</span>
                                <button className="slds-button slds-button_icon slds-button_icon-inverse slds-notify__close" onClick={() => setRegSuccess('')} style={{ float: 'right' }}>✕</button>
                            </div>
                        )}
                        {regError && !isRegisterOpen && !showPackageInterstitial && (
                            <div className="slds-notify slds-notify_alert slds-alert_error slds-m-bottom_medium" role="alert">
                                <span>{regError}</span>
                                <button className="slds-button slds-button_icon slds-button_icon-inverse slds-notify__close" onClick={() => setRegError('')} style={{ float: 'right' }}>✕</button>
                            </div>
                        )}
                        <div>
                            <CQPushPortal
                                canSchedule={portalPermissions.canSchedule}
                                canRegister={portalPermissions.canRegister}
                                isRegisterOpen={isRegisterOpen}
                                setIsRegisterOpen={setIsRegisterOpen}
                                oauthLoginUrl={oauthLoginUrl}
                                setOauthLoginUrl={setOauthLoginUrl}
                                regLoading={regLoading}
                                regError={regError}
                                currentUsername={currentUsername}
                                currentUserEmail={currentUserEmail}
                                registerTagNames={registerTagNames}
                                setRegisterTagNames={setRegisterTagNames}
                                registerTagError={registerTagError}
                                handleConnectOrg={handleConnectOrg}
                                showPackageInterstitial={showPackageInterstitial}
                                handlePackageInstallNow={handlePackageInstallNow}
                                handlePackageContinue={handlePackageContinue}
                                handlePackageBack={handlePackageBack}
                            />
                        </div>
                    </BrandBand>
                </IconSettings>
            </div>
        </>
    );

    /** Fetch the current Salesforce username for display/audit fields. */
    async function fetchCurrentUser() {
        try {
            const res = await SFConnection.getAxiosInstance().get('/userInfo');
            setCurrentUsername(res.data?.username || '');
            setCurrentUserEmail(res.data?.email || '');
        } catch {
            setCurrentUsername('');
            setCurrentUserEmail('');
        }
    }

    /**
     * Start OAuth flow for org registration after validating session.
     * Validates optional tag input before showing the package interstitial.
     */
    async function handleConnectOrg() {
        setRegLoading(true);
        setRegError('');
        setRegisterTagError('');

        const tagValidation = validateTagNames(registerTagNames);
        if (!tagValidation.isValid) {
            setRegisterTagError(tagValidation.error || 'Invalid tags');
            setRegLoading(false);
            return;
        }
        
        // Check if session is still valid before proceeding
        const isValid = await SFConnection.isSessionValid();
        if (!isValid) {
            setRegLoading(false);
            window.dispatchEvent(new CustomEvent('session-expired'));
            return;
        }

        // Show package interstitial instead of directly starting OAuth
        setRegLoading(false);
        setIsRegisterOpen(false);
        setShowPackageInterstitial(true);
    }

    /**
     * Handles the "Install Now" button click from the package interstitial.
     * Opens the Salesforce package installation URL in a new tab.
     */
    function handlePackageInstallNow() {
        try {
            const installUrl = getInstallUrl(oauthLoginUrl);
            window.open(installUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
            setRegError(t('push_portal_install_url_error'));
            console.error('Install URL error:', error);
        }
    }

    /**
     * Handles the "Already Installed - Continue" button click from the package interstitial.
     * Proceeds with the OAuth flow for org registration.
     */
    async function handlePackageContinue() {
        setRegLoading(true);
        setRegError('');

        const tagValidation = validateTagNames(registerTagNames);
        
        try {
            console.log('oauthurl', oauthLoginUrl);
            const { authorizationUrl } = await PushPortalServiceProvider.startOAuthFlow(
                currentUsername,
                oauthLoginUrl,
                tagValidation.normalized || undefined
            );
            window.location.href = authorizationUrl;
        } catch (err: any) {
            console.error('this is hte error while login', err);
            setRegError(err?.response?.data?.error || CQAppConstant.ERROR_MESSAGES.OAUTH_FAILED);
            setRegLoading(false);
            setShowPackageInterstitial(false);
            setIsRegisterOpen(true);
        }
    }

    /**
     * Handles the "Back" button click from the package interstitial.
     * Returns to the registration modal.
     */
    function handlePackageBack() {
        setShowPackageInterstitial(false);
        setIsRegisterOpen(true);
    }
}

export default CQPushPortalPage;
